import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, DialogActions, DialogContent, Stack, SxProps, Theme, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { BhiDialog, SelectionOutline } from 'src/UIKit';
import ChatFileIcon from '../../../../components/ChatFileIcon/view';
import { ChatBubble } from '../../../../components/PhoneUI/styles';
import useConfirmationDialog from '../../../../components/ConfirmationDialog/view';
import BannedWordsModal from '../../../../components/BannedWordsModal/view';
import { getSmsPartsCount } from '../../../../helpers/helpers';
import { getApplication } from '../../../applications/slices/applicationsSlice';
import { getCurrentCompanyId } from '../../../companies/slices/companiesSlice';
import { useGetUserPreferencesApi, useUpdateUserPreferencesApi } from '../../../../api/users';
import { TestSendPreferences, useSendTestMessageToContactApi } from '../../api';
import { DefaultCustomField, Message } from '../../../../constants/types';
import { formatCustomFieldToHtml } from '../../../../helpers/formatCustomField';
import { formatUnicodeStylesToHtml } from '../../../../components/TemplateText/helpers';
import { useBlocklistApi } from '../../../blocklist/api';
import { PhoneTypeahead } from './PhoneTypeahead';
import { extractPlaceholders, isSystemPlaceholder, renderTestMessageText } from './helpers';
import { testSendErrorMessage } from './errorMessages';

const RECENT_PHONES_CAP = 5;
const MMS_ATTACHMENT_PREVIEW_WIDTH = '120';

type SendTestMessageDialogProps = {
  open: boolean;
  onClose: () => void;
  messages: Message[];
  optOutText: string | undefined;
  metadata: DefaultCustomField[] | undefined;
};

export function SendTestMessageDialog({ open, onClose, messages, optOutText, metadata }: SendTestMessageDialogProps) {
  const companyId = useSelector(getCurrentCompanyId);
  const application = useSelector(getApplication);
  const getPreferences = useGetUserPreferencesApi();
  const updatePreferences = useUpdateUserPreferencesApi();
  const sendToContactApi = useSendTestMessageToContactApi();
  const validateText = useBlocklistApi().validate;
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const [phone, setPhone] = useState('');
  const [contactId, setContactId] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<TestSendPreferences>({ recentPhones: [] });
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [showBannedWordsModal, setShowBannedWordsModal] = useState(false);
  const [messageIndex, setMessageIndex] = useState<number | null>(messages.length === 1 ? 0 : null);
  const [selectedMessageIndexes, setSelectedMessageIndexes] = useState<number[]>(messages.length === 1 ? [0] : []);
  const [selectedMessagePosition, setSelectedMessagePosition] = useState(0);
  const message = messages[messageIndex ?? 0];
  const emptyPlaceholders = useMemo(
    () => extractPlaceholders(`${message.subject ?? ''} ${message.text}`)
      .filter(placeholder => !isSystemPlaceholder(placeholder)),
    [message.subject, message.text],
  );

  useEffect(() => {
    if (!open) return;
    setMessageIndex(messages.length === 1 ? 0 : null);
    setSelectedMessageIndexes(messages.length === 1 ? [0] : []);
    setSelectedMessagePosition(0);
    setPhone('');
    setContactId(null);
    setSendError(null);
    (async () => {
      try {
        const response = await getPreferences();
        const testSend = (response.data?.testSend ?? {}) as Partial<TestSendPreferences>;
        const loaded: TestSendPreferences = {
          recentPhones: testSend.recentPhones ?? [],
        };
        setPrefs(loaded);
      } catch {
        setPrefs({ recentPhones: [] });
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open || messageIndex === null) return;
    setSendError(null);
  }, [messageIndex]);

  useEffect(() => {
    setSendError(null);
  }, [phone]);

  // Opt-out text is appended only to the first message and only when it is enabled
  // (a disabled opt-out arrives here as `undefined`).
  const effectiveOptOutText = message.position === 1 ? optOutText ?? null : null;

  const estimatedCredits = useMemo(() => {
    const rendered = renderTestMessageText(message.text, {}, effectiveOptOutText);
    const renderedSubject = renderTestMessageText(message.subject, {});
    const hasNonTextPayload = !!message.links?.length || renderedSubject.trim().length > 0;
    const segments = getSmsPartsCount(message.type, rendered, hasNonTextPayload);
    const price = message.type === 'sms' ? application?.priceForSMS ?? 1 : application?.priceForMMS ?? 1;
    return segments * price;
  }, [message.text, message.subject, message.type, message.position, message.links, effectiveOptOutText, application]);

  const performSend = async (blocklistConfirmed = false) => {
    if (!companyId || !contactId) return;
    setSending(true);
    setSendError(null);
    try {
      const renderedText = renderTestMessageText(message.text, {}, effectiveOptOutText);
      const renderedSubject = renderTestMessageText(message.subject, {});

      if (!blocklistConfirmed) {
        await validateText({ texts: [renderedText, renderedSubject].filter(text => text.trim().length > 0) });
      }

      const response = await sendToContactApi({
        contactId,
        messageType: message.type,
        text: message.text || null,
        subject: message.subject || null,
        links: message.links ?? [],
        optOutText: effectiveOptOutText,
        blocklistConfirmed,
      }, companyId);

      if (response.data.status === 'accepted') {
        const recentPhones = [phone, ...prefs.recentPhones.filter(p => p !== phone)].slice(0, RECENT_PHONES_CAP);
        const nextPrefs = { recentPhones };
        try {
          await updatePreferences({ testSend: nextPrefs });
        } catch (error) {
          console.error('[test-send] preferences update failed:', error);
        }
        toast.success(`Test message sent (${response.data.parts} segment${response.data.parts === 1 ? '' : 's'})`);
        if (selectedMessagePosition < selectedMessageIndexes.length - 1) {
          const nextPosition = selectedMessagePosition + 1;
          setPrefs(nextPrefs);
          setSelectedMessagePosition(nextPosition);
          setMessageIndex(selectedMessageIndexes[nextPosition]);
        } else {
          onClose();
        }
      } else {
        console.error('[test-send] send failed:', response.data);
        setSendError(testSendErrorMessage(response.data.code));
      }
    } catch (error) {
      console.error('[test-send] send error:', error);
      const payload = error as { error?: string; code?: string | number };
      const code = payload.error ?? payload.code?.toString();
      if (code === 'blocklist_match' || payload.code === 422) {
        setShowBannedWordsModal(true);
        return;
      }
      setSendError(testSendErrorMessage(code));
    } finally {
      setSending(false);
    }
  };

  const handleSend = () => {
    if (emptyPlaceholders.length > 0) {
      openDialog({
        title: 'Some placeholder values are empty',
        content: 'This message contains merge tags without values. In a real campaign, only contacts that have values for these merge tags would receive the message. Send the test anyway?',
        confirmButtonText: 'Send',
        confirmButtonColor: 'primary',
        confirmButtonVariant: 'contained',
        cancelButtonColor: 'primary',
        cancelButtonVariant: 'outlined',
        onConfirm: performSend,
      });
      return;
    }
    performSend();
  };

  const handleContactChange = (nextPhone: string, nextContactId: string | null) => {
    setPhone(nextPhone);
    setContactId(nextContactId);
    setSendError(null);
  };

  const toggleMessageSelection = (index: number) => {
    setSelectedMessageIndexes(current => (
      current.includes(index)
        ? current.filter(selectedIndex => selectedIndex !== index)
        : [...current, index].sort((a, b) => a - b)
    ));
  };

  const startSelectedMessages = () => {
    if (selectedMessageIndexes.length === 0 || !contactId) return;
    setSelectedMessagePosition(0);
    setMessageIndex(selectedMessageIndexes[0]);
  };

  const renderPhonePicker = (sx?: SxProps<Theme>) => (
    <Box sx={{ flexShrink: 0, minWidth: 0, width: '100%', ...sx }}>
      <PhoneTypeahead
        companyId={companyId ?? ''}
        recentPhones={prefs.recentPhones}
        value={phone}
        onChange={handleContactChange}
      />
    </Box>
  );

  const formatPickerMessage = (value: string): string => {
    const formatted = value.replaceAll(/{{[^{}]+(?=})}}/g, match => {
      const fieldName = match.replace(/^{{|}}$/g, '');
      if (fieldName.includes('mention-link')) {
        return match;
      }
      return formatCustomFieldToHtml(match, metadata || [], { includeClass: false }) || match;
    });
    return formatUnicodeStylesToHtml(formatted);
  };

  const fullWidthBubbleSx = {
    boxSizing: 'border-box',
    margin: '5px 0',
    maxWidth: 'none',
    width: '100%',
  } as const;

  const renderedPreviewSubject = message.subject ?? '';
  const renderedPreviewText = renderTestMessageText(message.text, {}, effectiveOptOutText);

  return (
    <BhiDialog
      open={open}
      onClose={onClose}
      title={messageIndex !== null && selectedMessageIndexes.length > 1
        ? `Send Test Message (${selectedMessagePosition + 1} of ${selectedMessageIndexes.length})`
        : 'Send Test Message'}
      fullWidth
      maxWidth="sm"
    >
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          This is a test message only. The campaign is not live, and replies to this message will not be recorded as campaign responses.
        </Alert>

        {messageIndex === null ? (
          <Stack spacing={1.5}>
            {renderPhonePicker()}
            <Typography variant="subtitle2">Choose one or more messages to test</Typography>
            <Box sx={{ maxHeight: 300, overflowY: 'auto', p: 1 }}>
              <Stack spacing={1.5}>
                {messages.map((candidate, index) => {
                  const isSelected = selectedMessageIndexes.includes(index);
                  const messageNumber = candidate.position ?? index + 1;
                  return (
                    <Box
                      key={candidate.id ?? candidate.position ?? index}
                      sx={{ alignItems: 'center', display: 'flex', gap: 1, width: '100%' }}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleMessageSelection(index)}
                        inputProps={{ 'aria-label': `Select message ${messageNumber}` }}
                        sx={{ flexShrink: 0 }}
                      />
                      <SelectionOutline selected={isSelected} sx={{ flex: 1, minWidth: 0 }}>
                        <ChatBubble
                          $isUser={false}
                          role="button"
                          tabIndex={0}
                          aria-label={`Message ${messageNumber}`}
                          aria-pressed={isSelected}
                          onClick={() => toggleMessageSelection(index)}
                          onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              toggleMessageSelection(index);
                            }
                          }}
                          sx={fullWidthBubbleSx}
                        >
                          {candidate.linksWithMimeType?.map((item, attachmentIndex) => (
                            <ChatFileIcon
                              url={item.url}
                              mimeType={item.mimeType}
                              width={candidate.type === 'mms' ? MMS_ATTACHMENT_PREVIEW_WIDTH : undefined}
                              key={attachmentIndex}
                            />
                          ))}
                          {candidate.subject && (
                            <div
                              style={{ marginBottom: '6px' }}
                              dangerouslySetInnerHTML={{
                                __html: formatPickerMessage(candidate.subject),
                              }}
                            />
                          )}
                          {candidate.text && (
                            <div dangerouslySetInnerHTML={{
                              __html: formatPickerMessage(candidate.text),
                            }} />
                          )}
                        </ChatBubble>
                      </SelectionOutline>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        ) : <>
        {/* A single-message test chooses the recipient on the preview step. */}
        {messages.length === 1 && renderPhonePicker({ pb: 2 })}

        <Box boxSizing="border-box" flex={1} minHeight={0} p={1} width="100%" sx={{ overflowY: 'auto' }}>
          <ChatBubble $isUser={false} sx={fullWidthBubbleSx}>
            {message.linksWithMimeType?.map((item, attachmentIndex) => (
              <ChatFileIcon
                url={item.url}
                mimeType={item.mimeType}
                width={message.type === 'mms' ? MMS_ATTACHMENT_PREVIEW_WIDTH : undefined}
                key={attachmentIndex}
              />
            ))}
            {renderedPreviewSubject && (
              <div
                style={{ marginBottom: '6px' }}
                dangerouslySetInnerHTML={{ __html: formatPickerMessage(renderedPreviewSubject) }}
              />
            )}
            {renderedPreviewText && (
              <div dangerouslySetInnerHTML={{ __html: formatPickerMessage(renderedPreviewText) }} />
            )}
          </ChatBubble>
        </Box>

        {sendError && (
          <Typography variant="body2" color="error" sx={{ mt: 2, minWidth: 0, overflowWrap: 'anywhere' }}>
            {sendError}
          </Typography>
        )}
        <BannedWordsModal
          isOpen={showBannedWordsModal}
          onConfirm={() => {
            setShowBannedWordsModal(false);
            performSend(true);
          }}
          onCancel={() => setShowBannedWordsModal(false)}
        />
        <ConfirmationDialog />
        </>}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        {messageIndex === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
            <Button onClick={onClose} variant="outlined">Cancel</Button>
            <Button
              onClick={startSelectedMessages}
              variant="contained"
              disabled={selectedMessageIndexes.length === 0 || !contactId}
            >
              Continue ({selectedMessageIndexes.length})
            </Button>
          </Box>
        ) : (
          <>
            <Stack direction="row" spacing={2} alignItems="center">
              {messages.length > 1 && selectedMessagePosition === 0 && (
                <Button onClick={() => setMessageIndex(null)} variant="outlined">Back</Button>
              )}
              <Typography variant="caption" color="text.secondary">Estimated Credits: {estimatedCredits}</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button onClick={onClose} variant="outlined">Cancel</Button>
              <Button onClick={handleSend} variant="contained" disabled={sending || !contactId}>
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </Stack>
          </>
        )}
      </DialogActions>
    </BhiDialog>
  );
}
